<template>
    <wrapper-features-templates-operations
    :showLoader="showLoader"
    :showOverlayLoader="showOverlayLoader"
    :applyErrorState="applyErrorState && !showLoader"
    :customBreadcrumbs="{
        currentArea: props.selectedProduct.name,
        menuParent: t('credits.creditSolutions.subscribeProduct.customBreadcrumbs.menuParent')
    }"
    :feedbackOperationData="headerOperationData"
    :activeStep="activeStep"
    :showStepper="activeStep !== 3 && !applyErrorState && !showLoader && getAccountsBalances.length"
    :stepperParams="stepperParams"
    :showFooter="activeStep !== 3 && !showLoader && getAccountsBalances.length"
    :footerButtonsParams="footerButtonsParams.filter(obj => obj.step === activeStep)"
    :scrollValue="scrollValue"
    :tokenTitleAppend="t('credits.creditSolutions.subscribeProduct.tokenTitleAppend')"
    @challengeSuccessHandler="challengeSuccessHandler"
    @challengeErrorHandler="errorHandler($event, createCredit)"
    @clearScrollToValue="scrollValue = null"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <!-- Content -->
        <component
        v-if="!showLoader && !applyErrorState && getAccountsBalances.length"
        :is="getActiveStepComponent"
        :isMobile="getMobileAppState"
        :selectedProduct="selectedProduct"
        :formData="formData"
        :mandatoryList="mandatoryList"
        :showMandatoryErrors="showMandatoryErrors"
        :clearFormErrors="clearFormErrors"
        :currency="currency"
        :accountsBalances="getAccountsBalances"
        :plafond="getProductPlafond"
        :responseData="responseData"
        @scrollTo="scrollValue = $event"
        @resetShowMandatoryErrorsProp="showMandatoryErrors = false"
        @formErrorsCleared="clearFormErrors = false"
        @updateData="updateDataHandler($event)"/>
        <!-- *********************************************
        *                    Empty State                 *
        ********************************************** -->
		<template v-if="!showLoader && !applyErrorState && !getAccountsBalances.length" #empty-state-slot>
            <empty-state-generic
            illustration="monocrome/empty-state-accounts"
            :title = "t('accountsEmptyStateTitle')"
            :description = "t('accountsEmptyStateDescription')"/>
        </template>
    </wrapper-features-templates-operations>
    <!-- Feedback Toast -->
    <toasts-generic
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    :buttonsParams="feedbackToastData.buttons"
    @hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOperations from '@/components/wrappers/featuresTemplates/Operations'
import ToastsGeneric from '@/components/generic/toasts/Generic'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'

/* Assets and Helpers */
import { clearObject, atLeastOneIsEmpty } from '@/assets/scripts/objects'
import { emptyObjectConstructor, errorObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Vue */
import { ref, computed, defineAsyncComponent, onBeforeUnmount } from 'vue'

/* Store */
import { createDownloadGenericReceiptPdf } from '@/network/api/operations/operations.api.js'
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapActions & mapGetters */
const { requestProductPlafond, createCreditAccount, requestAccountsBalances, clearSubscribeProductStates } = mapActions(moduleMapper.CREDITS_CREDITS_SOLUTIONS)
const { getProductPlafond, getAccountsBalances } = mapGetters(moduleMapper.CREDITS_CREDITS_SOLUTIONS)
const { getMobileAppState } = mapGetters()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* props received
@selectedProduct { Object } - data received from productsList
@accountId {Number} - prop to selected account
*/
const props = defineProps({
    selectedProduct: Object
})

/* Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)
const applyErrorState = ref(false)
const scrollValue = ref(null)
const showMandatoryErrors = ref(false)
const clearFormErrors = ref(false)

const formData = ref({
    accountId: null,
    amount: null,
    conditions: null
})

const mandatoryList = ref([
    'accountId',
    'amount',
    'conditions'
])

const updateDataHandler = ({ fieldName, inputValue }) => {
    formData.value[fieldName] = inputValue
    if (fieldName === 'accountId') productPlafond()
}

/*************************************************
*                  Steps Data                    *
*************************************************/
const activeStep = ref(1)

const goToStep = stepNumber => {
    activeStep.value = stepNumber
    scrollValue.value = 0
}

const stepperParams = ref([
    { description: t('credits.creditSolutions.subscribeProduct.stepperParams.data') },
    { description: t('credits.creditSolutions.subscribeProduct.stepperParams.validation') }
])

const getActiveStepComponent = computed(() => {
    const stepNumber = activeStep.value
    return defineAsyncComponent(() => import('@/components/features/credits/subscribeProduct/Step' + stepNumber))
})

const currency = computed(() => {
    return getAccountsBalances.value.find(account => account.default).currency
})

/*************************************************
*                                                *
*                    Requests                    *
*                                                *
*************************************************/
const accountsBalances = async () => {
	showLoader.value = true
    applyErrorState.value = false

	try {
        await requestAccountsBalances()
        if (getAccountsBalances.value.length) {
            if (props.selectedProduct.accountId && getAccountsBalances.value.find(account => account.id === props.selectedProduct.accountId)) {
                /* Setting Initial Selected Account */
                formData.value.accountId = getAccountsBalances.value.find(account => account.id === props.selectedProduct.accountId).id
            } else {
                /* Setting Initial Selected Account */
                formData.value.accountId = getAccountsBalances.value.find(account => account.default).id
            }
            productPlafond('initialRequest')
            return
        }
		showLoader.value = false
	} catch (error) {
        if (error.toString() === 'Cancel') return
        applyErrorState.value = true
        errorHandler(error.response.data, accountsBalances)
	}
}

const productPlafond = async (initialRequest) => {
	initialRequest ? showLoader.value = true : showOverlayLoader.value = true
    applyErrorState.value = false

    const payload = {
        pid: props.selectedProduct.product.code,
        spid: props.selectedProduct.subProduct.code,
        params: { accountId: formData.value.accountId }
    }

    try {
        await requestProductPlafond(payload)
        initialRequest ? showLoader.value = false : showOverlayLoader.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        applyErrorState.value = true
        errorHandler(error.response.data, productPlafond)
    }
}

const createCredit = async () => {
    showOverlayLoader.value = true

    const payload = {
        pid: props.selectedProduct.product.code,
        spid: props.selectedProduct.subProduct.code,

        data: {
            accountId: formData.value.accountId,
            amount: formData.value.amount,
            duration: props.selectedProduct.maximumDuration,
            currency: currency.value
        }
    }

    try {
        await createCreditAccount(payload)
        showOverlayLoader.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, createCredit)
    }
}

/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())
const headerOperationData = ref(null)
const responseData = ref()
const showOperationsButtons = ref(false)

const challengeSuccessHandler = data => {
    responseData.value = {
        ...data.data,
        errorLevel: data.errorLevel,
        code: data.code
    }
    showOperationsButtons.value = data.data.hasReceipt

    headerOperationData.value = {
        status: data.errorLevel,
        title: data.message,
        buttonsData: !responseData.value.hasReceipt ? null
        : {
            iconsColor: 'var(--positive)',
            emailAction: 'sendOperationEmailNotification',
            smsAction: 'sendOperationSMSNotification',
            sendPayload: { operationId: data.data.operationId },
            downloadUrl: createDownloadGenericReceiptPdf({ params: { operationID: data.data.operationId } }),
            operationId: data.data.operationId
        }
    }

    goToStep(3)
}

const errorHandler = (data, retryFn) => {
	showLoader.value = false
    showOverlayLoader.value = false

    feedbackToastData.value = errorObjectConstructor(data, retryFn, () => { goToStep(1) })
	showFeedbackToast.value = true
}

/* Footer Buttons Data */
const footerButtonsParams = computed(() => {
    return [{
        label: t('credits.creditSolutions.subscribeProduct.footerButtons.clear'),
        step: 1,
        buttonPosition: 'left',
        callbackFunction: () => { clearAllFields() }
    }, {
        label: t('credits.creditSolutions.subscribeProduct.footerButtons.forward'),
        icon: 'arrow-right-full',
        buttonType: 'primary',
        iconPosition: 'right',
        step: 1,
        callbackFunction: () => { validateInputs() }
    }, {
        label: t('credits.creditSolutions.subscribeProduct.footerButtons.edit'),
        buttonPosition: !getMobileAppState.value ? 'right' : 'left',
        icon: 'arrow-left-full',
        step: 2,
        callbackFunction: () => { goToStep(1) }
    }, {
        label: t('credits.creditSolutions.subscribeProduct.footerButtons.validate'),
        icon: 'arrow-right-full',
        buttonType: 'primary',
        iconPosition: 'right',
        step: 2,
        callbackFunction: () => { createCredit() }
    }]
})

const clearAllFields = () => {
    formData.value = clearObject(formData.value)
    formData.value = {
        ...formData.value,
        accountId: getAccountsBalances.value.find(account => account.default).id,
        currency: currency.value,
        amount: null,
        conditions: false
    }
    productPlafond()
    clearFormErrors.value = true
    scrollValue.value = 0
}

const validateInputs = () => {
    const invalidAmount = formData.value.amount < props.selectedProduct.minimumAmount || formData.value.amount > props.selectedProduct.maximumAmount

    atLeastOneIsEmpty(formData.value, mandatoryList.value) || invalidAmount || !formData.value.conditions || (parseInt(formData.value.amount) > getProductPlafond.value.amount) ? showMandatoryErrors.value = true : goToStep(2)
}

accountsBalances()

onBeforeUnmount(() => {
    clearSubscribeProductStates()
})
</script>

<style lang="scss" scoped>
.empty-state-wrapper {
    @include commonEnteringAnimation;
}
</style>
